import { recordStatusLabel } from "../../domain/shared/status-labels.js";
import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { platform } from "../../app/browser-platform.js";
import type { WorkOrder, SubWorkOrder, Area } from "../../administration/admin-domain.js";
import type { Professional } from "../../domain/identity/professional.js";
import type { Project } from "../../domain/project/project-domain.js";
import type {
  UnitAreaLike,
  WorkOrderProjectLinkLike,
  WorkOrderTreeNode
} from "../../domain/rdo/sub-os-context.js";
import {
  autoSelectUnitArea,
  keepProjectOnWorkOrderChange,
  keepUnitAreaOnProjectChange,
  linkableProjectsForWorkOrder,
  projectOptionsForWorkOrder,
  selectableProjectIds,
  unitAreaOptionsForProject,
  NO_PROJECTS_FOR_WORK_ORDER_MESSAGE,
  NO_UNIT_AREAS_FOR_PROJECT_MESSAGE
} from "../../domain/rdo/sub-os-form-cascade.js";
import type { LinkableProject } from "../../domain/rdo/sub-os-form-cascade.js";

export default class AdminOsPage extends HTMLElement {
  private workOrders: WorkOrder[] = [];
  private subWorkOrders: SubWorkOrder[] = [];
  private areas: Area[] = [];
  private professionals: Professional[] = [];
  private clients: any[] = [];
  private projects: Project[] = [];
  /** Árvore OS → Projetos → Sub-OS, a hierarquia oficial. */
  private workOrderTree: WorkOrderTreeNode[] = [];
  private treeExpanded = new Set<string>();
  private searchQuery = "";
  private statusFilter = "ALL";
  private clientFilter = "ALL";
  private projectFilter = "ALL";
  private activeTab: "OS" | "SUB_OS" = "OS";

  private isOsModalOpen = false;
  private editingOs: WorkOrder | null = null;
  private selectedOsModalClientId = "";
  private selectedOsModalProjectId = "";

  private isSubOsModalOpen = false;
  private editingSubOs: SubWorkOrder | null = null;
  private selectedSubOsModalClientId = "";
  /**
   * Cascata do cadastro de Sub-OS: OS → Projeto → Unidade/Área.
   *
   * Guarda também o que já foi digitado: cada passo da cascata redesenha a
   * tela, e o texto do usuário não pode se perder no caminho.
   */
  private subOsForm = {
    workOrderId: "",
    projectId: "",
    unitAreaId: "",
    professionalId: "",
    description: "",
    status: "OPEN",
    startDate: "",
    endDate: ""
  };
  private unitAreas: UnitAreaLike[] = [];
  private workOrderProjectLinks: WorkOrderProjectLinkLike[] = [];

  /** Modal "Vincular Projeto à OS" — substitui o prompt numerado. */
  private linkProjectModal = { isOpen: false, workOrderId: "", search: "", selected: new Set(), error: "" } as
    { isOpen: boolean; workOrderId: string; search: string; selected: Set<string>; error: string };

  private deleteModal = { isOpen: false, type: "" as "OS" | "SUB_OS" | "", id: "", title: "", message: "", error: "" };
  /** Aviso da própria tela — substitui o alert() nativo. */
  private notice = { isOpen: false, title: "", message: "" };

  /** Colaboradores de uma Sub-OS — quem pode apontar RDO nela. */
  private collaboratorsModal = {
    isOpen: false,
    subOsId: "",
    accesses: [] as { id: string; professionalId: string; status: string }[],
    error: ""
  };
  private boundHandleMasterDataUpdated: (e: Event) => void;

  constructor() {
    super();
    this.boundHandleMasterDataUpdated = this.handleMasterDataUpdated.bind(this);
  }

  private handleMasterDataUpdated(e: Event) {
    this.loadData();
  }

  public connectedCallback(): void {
    this.parseQueryParams();
    this.loadData();
    window.addEventListener("active-client-changed", () => this.loadData());
    window.addEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  public disconnectedCallback(): void {
    window.removeEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  private parseQueryParams(): void {
    try {
      const hash = window.location.hash;
      const search = window.location.search;
      let queryStr = "";
      if (hash && hash.includes("?")) {
        queryStr = hash.split("?")[1] || "";
      } else if (search) {
        queryStr = search.replace(/^\?/, "");
      }
      if (queryStr) {
        const params = new URLSearchParams(queryStr);
        const pId = params.get("projectId");
        if (pId) {
          this.projectFilter = pId;
        }
        const cId = params.get("clientId");
        if (cId) {
          this.clientFilter = cId;
          this.selectedOsModalClientId = cId;
        }
        const newForPrj = params.get("newForProject");
        if (newForPrj) {
          this.projectFilter = newForPrj;
          this.selectedOsModalProjectId = newForPrj;
          if (cId) this.selectedOsModalClientId = cId;
          this.isOsModalOpen = true;
          this.editingOs = null;
        }
      }
    } catch (e) {
      console.warn("Could not parse query params", e);
    }
  }

  private async loadData(): Promise<void> {
    try {
      const ctx = contextStore.get();
      // FRENTE C — OS MULTICLIENTE: o filtro local da tela tem prioridade sobre
      // o activeClientId global. Quando clientFilter é "ALL", não passa clientId
      // ao service para que ele liste OS de todos os clientes autorizados (o
      // service já valida autorização no backend). Quando há filtro explícito,
      // usa-o como clientId para recortar a listagem no banco.
      const effectiveClientId = this.clientFilter && this.clientFilter !== "ALL"
        ? this.clientFilter
        : undefined;
      const dummyContext = {
        activeClientId: effectiveClientId || ctx.activeClientId || "",
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;
      const [wos, subs, ars, profs, cls, prjs] = await Promise.all([
        platform.adminService.listWorkOrders(dummyContext, effectiveClientId),
        platform.adminService.listSubWorkOrders(dummyContext),
        platform.adminService.listAreas(dummyContext),
        // REGRA V4-G/H: PROFESSIONAL É GLOBAL. Carregar SEM clientId.
        // Usa contexto global (sem activeClientId) para evitar filtro por tenant.
        platform.adminService.listProfessionals({ ...dummyContext, activeClientId: "" } as any),
        platform.adminService.listAuthorizedClients(dummyContext).catch(() => []),
        platform.projectService.listProjects(undefined, undefined, dummyContext).catch(() => [])
      ]);
      this.workOrders = wos;
      this.subWorkOrders = subs;
      this.areas = ars;
      this.professionals = profs;
      this.clients = cls || [];
      this.projects = prjs || [];

      this.unitAreas = (await platform.projectService.listUnitAreas(undefined, undefined, dummyContext).catch((e: unknown) => {
        console.error("[OS] Falha ao carregar Unidades/Áreas", e);
        return [];
      })) as unknown as UnitAreaLike[];
      this.workOrderProjectLinks = await platform.rdoService
        .listWorkOrderProjectLinks(dummyContext)
        .catch((e: unknown) => {
          console.error("[OS] Falha ao carregar vínculos OS ↔ Projeto", e);
          return [] as WorkOrderProjectLinkLike[];
        });

      // Árvore da hierarquia oficial OS → Projetos → Sub-OS.
      this.workOrderTree = await platform.rdoService.buildWorkOrderTree(dummyContext).catch((e: any) => {
        console.error("[OS] Falha ao montar a árvore OS → Projetos → Sub-OS", e);
        return [];
      });

      if (this.selectedOsModalProjectId && !this.selectedOsModalClientId) {
        const foundP = this.projects.find(p => p.id === this.selectedOsModalProjectId);
        if (foundP?.clientId) {
          this.selectedOsModalClientId = foundP.clientId;
        }
      }

      this.render();
    } catch (e) {
      console.error("Failed to load work orders", e);
    }
  }

  /**
   * Árvore OS → PROJETOS → SUB-OS.
   *
   * É a hierarquia oficial: a OS é o nível superior e possui vários Projetos;
   * cada Projeto possui as suas Sub-OS.
   */
  private renderHierarchyTree(): string {
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));

    const visible = this.workOrderTree.filter(node =>
      this.clientFilter === 'ALL' ||
      this.workOrders.find(wo => wo.id === node.workOrderId)?.clientId === this.clientFilter
    );

    if (visible.length === 0) return '';

    // A árvore começa no Cliente: cada Cliente tem as suas OS, e cada OS os
    // seus Projetos. As OS sem Cliente resolvido ficam agrupadas à parte em vez
    // de serem penduradas num Cliente qualquer.
    const byClient = new Map<string, typeof visible>();
    for (const node of visible) {
      const clientId = this.workOrders.find(wo => wo.id === node.workOrderId)?.clientId || '';
      const bucket = byClient.get(clientId) ?? [];
      bucket.push(node);
      byClient.set(clientId, bucket);
    }

    const clientName = (clientId: string) => {
      if (!clientId) return 'Cliente não identificado';
      const client = this.clients.find((c: any) => c.id === clientId);
      return client?.name || client?.legalName || clientId;
    };

    const groups = [...byClient.entries()].sort((a, b) =>
      clientName(a[0]).localeCompare(clientName(b[0]), 'pt-BR', { numeric: true })
    );

    const renderNodes = (nodes: typeof visible) => nodes.map(node => {
          const open = this.treeExpanded.has(node.workOrderId);
          const projectCount = node.projects.length;
          return `
            <div style="border-bottom: 1px solid var(--color-bg-secondary); padding: 8px 0;">
              <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
                <button type="button" class="btn-tree-toggle" data-id="${esc(node.workOrderId)}" style="background: none; border: none; cursor: pointer; color: var(--color-text-primary); font-weight: 700; font-size: 0.9rem; padding: 0;">
                  ${open ? '▾' : '▸'} ${esc(node.workOrderCode)}
                </button>
                <span style="font-size: 0.74rem; color: var(--color-text-secondary);">${projectCount} projeto(s)</span>
                <button type="button" class="btn-link-project" data-id="${esc(node.workOrderId)}" style="background: none; border: 1px solid var(--color-graphite-300); border-radius: 4px; color: var(--color-text-muted); cursor: pointer; font-size: 0.72rem; padding: 3px 9px;">
                  + Vincular Projeto
                </button>
              </div>
              ${open ? `
                <div style="margin: 8px 0 0 22px;">
                  ${projectCount === 0 ? `
                    <div style="font-size: 0.78rem; color: var(--color-text-secondary);">Nenhum Projeto vinculado a esta OS.</div>
                  ` : node.projects.map(project => `
                    <div style="margin-bottom: 10px;">
                      <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                        <span style="font-size: 0.84rem; font-weight: 600; color: var(--ia-primary);">├─ ${esc(project.projectLabel)}</span>
                        <button type="button" class="btn-tree-open-project" data-project="${esc(project.projectId)}" style="background:none; border:1px solid var(--color-graphite-300); border-radius:4px; color:var(--color-text-muted); cursor:pointer; font-size:0.68rem; padding:2px 8px;">ABRIR</button>
                        <button type="button" class="btn-tree-new-sub-os" data-id="${esc(node.workOrderId)}" data-project="${esc(project.projectId)}" style="background:none; border:1px solid var(--color-graphite-300); border-radius:4px; color:var(--color-text-muted); cursor:pointer; font-size:0.68rem; padding:2px 8px;">NOVA SUB-OS</button>
                        <button type="button" class="btn-tree-unlink-project" data-id="${esc(node.workOrderId)}" data-project="${esc(project.projectId)}" ${project.subOrders.length > 0 ? 'disabled title="Este Projeto possui Sub-OS nesta OS e não pode ser desvinculado."' : ''} style="background:none; border:1px solid var(--color-graphite-300); border-radius:4px; color:${project.subOrders.length > 0 ? 'var(--color-graphite-300)' : 'var(--color-text-muted)'}; cursor:${project.subOrders.length > 0 ? 'not-allowed' : 'pointer'}; font-size:0.68rem; padding:2px 8px;">DESVINCULAR</button>
                      </div>
                      <div style="margin-left: 18px; margin-top: 4px;">
                        ${project.subOrders.length === 0
                          ? `<div style="font-size: 0.76rem; color: var(--color-text-secondary);">sem Sub-OS</div>`
                          : project.subOrders.map(sub => {
                            const inactive = this.subWorkOrders.find(s => s.id === sub.id)?.status === 'CANCELLED';
                            return `
                            <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap; padding:2px 0;">
                              <span style="font-size: 0.78rem; color: ${inactive ? 'var(--color-text-secondary)' : 'var(--color-text-primary)'}; ${inactive ? 'text-decoration: line-through;' : ''}">└─ ${esc(sub.code)}${sub.description ? ` — ${esc(sub.description)}` : ''}</span>
                              ${inactive ? `<span style="font-size:0.64rem; font-weight:700; color:var(--status-danger-solid); letter-spacing:0.05em;">INATIVA</span>` : ''}
                              <button type="button" class="btn-edit-sub-os" data-id="${esc(sub.id)}" style="background:none; border:1px solid var(--color-graphite-300); border-radius:4px; color:var(--color-text-muted); cursor:pointer; font-size:0.66rem; padding:1px 7px;">EDITAR</button>
                              <button type="button" class="btn-sub-os-collaborators" data-id="${esc(sub.id)}" style="background:none; border:1px solid var(--color-graphite-300); border-radius:4px; color:var(--color-text-muted); cursor:pointer; font-size:0.66rem; padding:1px 7px;">COLABORADORES</button>
                              <button type="button" class="btn-toggle-sub-os" data-id="${esc(sub.id)}" data-next="${inactive ? 'OPEN' : 'CANCELLED'}" style="background:none; border:1px solid var(--color-graphite-300); border-radius:4px; color:var(--color-text-muted); cursor:pointer; font-size:0.66rem; padding:1px 7px;">${inactive ? 'ATIVAR' : 'INATIVAR'}</button>
                            </div>`;
                          }).join('')}
                      </div>
                    </div>
                  `).join('')}
                  ${node.unassignedSubOrders.length > 0 ? `
                    <div style="margin-top: 8px;">
                      <div style="font-size: 0.78rem; font-weight: 600; color: var(--status-warning-solid, #AEAEAE);">Sub-OS sem Projeto definido (registros anteriores)</div>
                      <div style="margin-left: 18px;">
                        ${node.unassignedSubOrders.map(sub => `<div style="font-size: 0.78rem; color: var(--color-text-secondary);">└─ ${esc(sub.code)}</div>`).join('')}
                      </div>
                    </div>` : ''}
                </div>` : ''}
            </div>`;
    }).join('');

    return `
      <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 16px;">
        <div style="font-size: 0.72rem; letter-spacing: 0.09em; text-transform: uppercase; font-weight: 700; color: var(--color-text-secondary); margin-bottom: 12px;">
          Hierarquia — Cliente › OS › Projetos › Sub-OS
        </div>
        ${groups.map(([clientId, nodes]) => `
          <div style="margin-bottom: 14px;">
            <div style="display: flex; align-items: center; gap: 8px; padding-bottom: 6px; border-bottom: 1px solid var(--color-graphite-200);">
              <span style="font-size: 0.86rem; font-weight: 700; color: var(--color-text-primary);">${esc(clientName(clientId))}</span>
              <span style="font-size: 0.72rem; color: var(--color-text-secondary);">${nodes.length} OS</span>
            </div>
            <div style="margin-left: 14px;">
              ${renderNodes(nodes)}
            </div>
          </div>`).join('')}
      </div>`;
  }

  private render(): void {
    const areaMap = new Map(this.areas.map(a => [a.id, a.name]));
    const osMap = new Map(this.workOrders.map(wo => [wo.id, wo.code]));
    const clientMap = new Map(this.clients.map(c => [c.id, c.name]));
    const projectMap = new Map(this.projects.map(p => [p.id, p]));
    const projectLabelMap = new Map(this.projects.map(p => [p.id, `${p.code} - ${p.name}`]));

    const filteredOs = this.workOrders.filter(wo => {
      const matchesClient = this.clientFilter === "ALL" || wo.clientId === this.clientFilter;
      const matchesProject = this.projectFilter === "ALL" || wo.projectId === this.projectFilter;
      const matchesStatus = this.statusFilter === "ALL" || wo.status === this.statusFilter;
      const term = this.searchQuery.toLowerCase();
      const matchesSearch = !term ||
        wo.code.toLowerCase().includes(term) ||
        (wo.description && wo.description.toLowerCase().includes(term));
      return matchesClient && matchesProject && matchesStatus && matchesSearch;
    });

    const filteredSubOs = this.subWorkOrders.filter(sub => {
      const matchesClient = this.clientFilter === "ALL" || sub.clientId === this.clientFilter;
      const matchesStatus = this.statusFilter === "ALL" || sub.status === this.statusFilter;
      const term = this.searchQuery.toLowerCase();
      const matchesSearch = !term ||
        sub.code.toLowerCase().includes(term) ||
        (sub.description && sub.description.toLowerCase().includes(term));
      return matchesClient && matchesStatus && matchesSearch;
    });

    const activeFilteredProject = this.projectFilter !== "ALL" ? projectMap.get(this.projectFilter) : null;

    this.innerHTML = `
      <div style="display: flex; flex-direction: column; height: 100%; gap: 16px; background: var(--color-bg-primary); padding: 24px;">
        <header style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 16px; flex-wrap: wrap; gap: 12px;">
          <div>
            <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
              <h1 style="color: var(--color-text-primary); font-size: 1.5rem; font-weight: 700; margin: 0;">Ordens de Serviço (OS) & Sub-OS</h1>
              <span style="font-size: 0.75rem; background: var(--color-surface-secondary); color: var(--ia-primary); border: 1px solid var(--color-border-subtle); padding: 2px 8px; border-radius: 6px; font-weight: 600;">
                Vinculação Obrigatória com Projeto
                Vinculação Obrigatória com Projeto
              </span>
            </div>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin-top: 4px;">Gerenciamento de Ordens de Serviço mestres vinculadas aos Projetos do Cliente e Sub-OS.</p>
          </div>
          <div style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
            <button id="btn-goto-projects" style="background-color: var(--color-bg-secondary); color: var(--color-text-muted); border: 1px solid var(--color-graphite-300); padding: 8px 14px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 6px;">
              <span><i data-lucide="folder" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> Ver Aba de Projetos</span>
            </button>
            <button id="btn-new-os" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 6px;">
              <span>+ Nova OS</span>
            </button>
            <button id="btn-new-sub-os" style="background-color: var(--color-primary); color: var(--color-text-primary); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 6px;">
              <span>+ Nova Sub-OS</span>
            </button>
          </div>
        </header>

        <!-- Tabs & Filters -->
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;">
          <div style="display: flex; background: var(--color-graphite-200); border-radius: 6px; padding: 2px;">
            <button id="tab-os" style="background: ${this.activeTab === 'OS' ? 'white' : 'transparent'}; color: ${this.activeTab === 'OS' ? 'var(--color-text-on-light)' : 'var(--color-graphite-500)'}; border: none; border-radius: 4px; padding: 6px 16px; font-weight: 600; font-size: 0.875rem; cursor: pointer;">
              Ordens de Serviço (${this.workOrders.length})
            </button>
            <button id="tab-sub-os" style="background: ${this.activeTab === 'SUB_OS' ? 'white' : 'transparent'}; color: ${this.activeTab === 'SUB_OS' ? 'var(--color-text-on-light)' : 'var(--color-graphite-500)'}; border: none; border-radius: 4px; padding: 6px 16px; font-weight: 600; font-size: 0.875rem; cursor: pointer;">
              Sub-OS (${this.subWorkOrders.length})
            </button>
          </div>

          <div style="display: flex; gap: 8px; flex: 1; max-width: 850px; flex-wrap: wrap;">
            <select id="select-client" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; background: var(--color-surface-primary);">
              <option value="ALL" ${this.clientFilter === "ALL" ? "selected" : ""}>Todos os Clientes</option>
              ${this.clients.map(c => `<option value="${c.id}" ${this.clientFilter === c.id ? "selected" : ""}>${c.name}</option>`).join("")}
            </select>
            <select id="select-project" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; background: var(--color-surface-primary); max-width: 220px;">
              <option value="ALL" ${this.projectFilter === "ALL" ? "selected" : ""}>Todos os Projetos</option>
              ${this.projects
                .filter(p => this.clientFilter === "ALL" || p.clientId === this.clientFilter)
                .map(p => `<option value="${p.id}" ${this.projectFilter === p.id ? "selected" : ""}>${p.code} - ${p.name}</option>`).join("")}
            </select>
            <input type="text" id="input-search" placeholder="Buscar por código ou descrição..." value="${this.searchQuery}" style="flex: 1; min-width: 170px; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
            <select id="select-status" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; background: var(--color-surface-primary);">
              <option value="ALL" ${this.statusFilter === "ALL" ? "selected" : ""}>Todos os Status</option>
              <option value="OPEN" ${this.statusFilter === "OPEN" ? "selected" : ""}>Abertas (OPEN)</option>
              <option value="COMPLETED" ${this.statusFilter === "COMPLETED" ? "selected" : ""}>Concluídas</option>
              <option value="CANCELLED" ${this.statusFilter === "CANCELLED" ? "selected" : ""}>Canceladas</option>
            </select>
          </div>
        </div>

        ${this.renderHierarchyTree()}

        ${activeFilteredProject ? `
          <div style="display: flex; align-items: center; justify-content: space-between; background: var(--status-success-bg); border: 1px solid var(--status-success-border); padding: 10px 16px; border-radius: 8px; font-size: 0.875rem;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <span style="font-weight: 700; color: var(--status-success-text);"><i data-lucide="folder" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> Filtrando por Projeto:</span>
              <span style="background: var(--color-surface-primary); padding: 3px 10px; border-radius: 6px; border: 1px solid var(--status-success-border); color: var(--status-success-text); font-weight: 600;">
                ${activeFilteredProject.code} — ${activeFilteredProject.name}
              </span>
              <a href="#/management/projects/${activeFilteredProject.id}" style="color: var(--color-ink); text-decoration: underline; font-size: 0.8125rem; margin-left: 6px;">
                Ver Detalhes do Projeto &rarr;
              </a>
            </div>
            <button id="btn-clear-project-filter" style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-300); color: var(--color-text-secondary); padding: 4px 10px; border-radius: 6px; font-size: 0.75rem; font-weight: 600; cursor: pointer;">
              ✕ Limpar Filtro de Projeto
            </button>
          </div>
        ` : ''}

        <!-- Table Container -->
        <div style="flex: 1; background: var(--color-surface-primary); border-radius: 8px; border: 1px solid var(--color-graphite-200); overflow-y: auto;">
          ${this.activeTab === 'OS' ? `
            <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
              <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200); position: sticky; top: 0;">
                <tr>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">CÓDIGO OS</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">CLIENTE</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">PROJETO VINCULADO</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">ÁREA</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">DESCRIÇÃO / ESCOPO</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">PERÍODO</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">STATUS</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600; text-align: right;">AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                ${filteredOs.length === 0 ? `
                  <tr>
                    <td colspan="8" style="padding: 32px; text-align: center; color: var(--color-graphite-400);">Nenhuma Ordem de Serviço encontrada.</td>
                  </tr>
                ` : filteredOs.map(wo => {
                  const prj = wo.projectId ? projectMap.get(wo.projectId) : null;
                  return `
                  <tr style="border-bottom: 1px solid var(--color-bg-secondary);">
                    <td style="padding: 12px 16px; font-family: monospace; font-weight: 700; color: var(--color-ink);">${wo.code}</td>
                    <td style="padding: 12px 16px; color: var(--color-text-muted); font-weight: 500;">${clientMap.get(wo.clientId) || wo.clientId || "-"}</td>
                    <td style="padding: 12px 16px;">
                      ${wo.projectId ? `
                        <a href="#/management/projects/${wo.projectId}" title="Ver detalhes do Projeto ${prj ? prj.name : wo.projectId}" style="display: inline-flex; align-items: center; gap: 5px; text-decoration: none; font-weight: 600; color: var(--ia-primary); background: rgba(199,167,91,0.15); padding: 4px 8px; border-radius: 6px; font-size: 0.8125rem; border: 1px solid var(--color-border-subtle); transition: background 0.15s;">
                          <i data-lucide="folder" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${prj ? `${prj.code} - ${prj.name}` : (projectLabelMap.get(wo.projectId) || wo.projectId)}
                        </a>
                      ` : `
                        <span style="display: inline-flex; align-items: center; gap: 4px; color: var(--status-danger-solid); font-size: 0.75rem; font-weight: 600; background: var(--status-danger-bg); padding: 3px 8px; border-radius: 6px; border: 1px solid var(--status-danger-border);">
                          ⚠️  Não Vinculado
                        </span>
                      `}
                    </td>
                    <td style="padding: 12px 16px; color: var(--color-text-muted); font-weight: 500;">${wo.areaId ? (areaMap.get(wo.areaId) || wo.areaId) : "-"}</td>
                    <td style="padding: 12px 16px; color: var(--color-text-secondary);">${wo.description || "-"}</td>
                    <td style="padding: 12px 16px; color: var(--color-text-secondary); font-size: 0.8125rem;">
                      ${wo.startDate ? new Date(wo.startDate).toLocaleDateString('pt-BR') : '-'} até ${wo.endDate ? new Date(wo.endDate).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td style="padding: 12px 16px;">
                      <span style="background: ${wo.status === 'OPEN' ? 'rgba(2, 132, 199, 0.1)' : wo.status === 'CLOSED' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}; color: ${wo.status === 'OPEN' ? 'var(--color-ink)' : wo.status === 'CLOSED' ? 'var(--status-success-solid)' : 'var(--status-danger-solid)'}; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">
                        ${recordStatusLabel(wo.status)}
                      </span>
                    </td>
                    <td style="padding: 12px 16px; text-align: right; white-space: nowrap;">
                      <button class="btn-edit-os" data-id="${wo.id}" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--color-text-muted); margin-right: 4px;">Editar</button>
                      ${wo.status === 'OPEN' ? `
                        <button class="btn-inactivate-os" data-id="${wo.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text); margin-right: 4px;">Cancelar</button>
                      ` : ''}
                      <button class="btn-delete-os" data-id="${wo.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-solid); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text);">Excluir</button>
                    </td>
                  </tr>
                `;
                }).join("")}
              </tbody>
            </table>
          ` : `
            <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
              <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200); position: sticky; top: 0;">
                <tr>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">CÓDIGO SUB-OS</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">CLIENTE</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">HIERARQUIA (OS › PROJETO › UNIDADE)</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">DESCRIÇÃO</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">PROFISSIONAL</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">PERÍODO</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">STATUS</th>
                  <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600; text-align: right;">AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                ${filteredSubOs.length === 0 ? `
                  <tr>
                    <td colspan="8" style="padding: 32px; text-align: center; color: var(--color-graphite-400);">Nenhuma Sub-OS cadastrada.</td>
                  </tr>
                ` : filteredSubOs.map(sub => `
                  <tr style="border-bottom: 1px solid var(--color-bg-secondary);">
                    <td style="padding: 12px 16px; font-family: monospace; font-weight: 700; color: var(--ia-primary);">${sub.code}</td>
                    <td style="padding: 12px 16px; color: var(--color-text-muted); font-weight: 500;">${clientMap.get(sub.clientId) || sub.clientId || "-"}</td>
                    <td style="padding: 12px 16px; color: var(--color-text-muted); font-weight: 600; font-size: 0.8125rem;">${this.formatSubOsHierarchy(sub)}</td>
                    <td style="padding: 12px 16px; color: var(--color-text-secondary);">${sub.description || "-"}</td>
                    <td style="padding: 12px 16px; color: var(--color-text-muted);">${this.professionals.find(p => p.id === sub.professionalId)?.name || 'Desconhecido'}</td>
                    <td style="padding: 12px 16px; color: var(--color-text-secondary); font-size: 0.8125rem;">
                      ${sub.startDate ? new Date(sub.startDate).toLocaleDateString('pt-BR') : '-'} até ${sub.endDate ? new Date(sub.endDate).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td style="padding: 12px 16px;">
                      <span style="background: ${sub.status === 'OPEN' ? 'rgba(2, 132, 199, 0.1)' : sub.status === 'CLOSED' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}; color: ${sub.status === 'OPEN' ? 'var(--color-ink)' : sub.status === 'CLOSED' ? 'var(--status-success-solid)' : 'var(--status-danger-solid)'}; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">
                        ${recordStatusLabel(sub.status)}
                      </span>
                    </td>
                    <td style="padding: 12px 16px; text-align: right; white-space: nowrap;">
                      <button class="btn-edit-sub-os" data-id="${sub.id}" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--color-text-muted); margin-right: 4px;">Editar</button>
                      <button class="btn-sub-os-collaborators" data-id="${sub.id}" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--color-text-muted); margin-right: 4px;">Colaboradores</button>
                      ${sub.status === 'OPEN' ? `
                        <button class="btn-inactivate-sub-os" data-id="${sub.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text); margin-right: 4px;">Cancelar</button>
                      ` : ''}
                      <button class="btn-delete-sub-os" data-id="${sub.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-solid); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text);">Excluir</button>
                    </td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          `}
        </div>

        ${this.isOsModalOpen ? this.renderOsModal() : ""}
        ${this.isSubOsModalOpen ? this.renderSubOsModal() : ""}
        ${this.linkProjectModal.isOpen ? this.renderLinkProjectModal() : ""}
        ${this.collaboratorsModal.isOpen ? this.renderCollaboratorsModal() : ""}
        ${this.notice.isOpen ? this.renderNoticeModal() : ""}
        ${this.deleteModal.isOpen ? this.renderDeleteModal() : ""}
      </div>
    `;

    this.attachEvents();
  }

  private renderOsModal(): string {
    const isEdit = !!this.editingOs;
    const currentClientId = this.editingOs ? this.editingOs.clientId : this.selectedOsModalClientId;
    const currentProjectId = this.editingOs ? this.editingOs.projectId : this.selectedOsModalProjectId;
    const availableProjects = currentClientId ? this.projects.filter(p => p.clientId === currentClientId) : [];
    const availableAreas = currentClientId ? this.areas.filter(a => a.clientId === currentClientId) : [];

    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 520px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
            <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">${isEdit ? 'Editar Ordem de Serviço' : 'Nova Ordem de Serviço'}</h2>
            <button class="btn-close-os-modal" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
          </div>
          
          <div id="os-modal-error" style="display: none; background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px; padding: 10px 14px; font-size: 0.8125rem; color: var(--status-danger-text); margin-bottom: 16px;"></div>

          <form id="form-os" style="display: flex; flex-direction: column; gap: 16px;">
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Cliente *</label>
              <select id="modal-os-client" ${isEdit ? 'disabled' : 'required'} style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: ${isEdit ? 'var(--color-bg-primary)' : 'white'};">
                <option value="">Selecione o Cliente...</option>
                ${this.clients.map(c => `<option value="${c.id}" ${currentClientId === c.id ? 'selected' : ''}>${c.name}</option>`).join("")}
              </select>
            </div>

            ${isEdit ? `
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Código da OS</label>
              <input type="text" value="${this.editingOs?.code || ''}" readonly style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background-color: var(--color-bg-primary); color: var(--color-graphite-400);">
            </div>` : ''}

            <div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                <label style="font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted);">
                  Projeto Vinculado <span style="color: var(--color-text-secondary); font-weight: 500;">(opcional — pode ser vinculado depois)</span>
                </label>
                <a href="#/management/projects" style="font-size: 0.75rem; color: var(--color-ink); text-decoration: none; font-weight: 500;">+ Gerenciar Projetos</a>
              </div>
              <select id="modal-os-project" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: ${!currentClientId ? 'var(--color-bg-primary)' : 'white'};" ${!currentClientId ? 'disabled' : ''}>
                <option value="">${!currentClientId ? 'Selecione um cliente primeiro...' : 'Nenhum (vincular depois)'}</option>
                ${availableProjects.map(p => `<option value="${p.id}" ${currentProjectId === p.id ? 'selected' : ''}>${p.code} - ${p.name}</option>`).join("")}
              </select>
              ${currentClientId && availableProjects.length === 0 ? `
                <div style="margin-top: 6px; padding: 8px 10px; background: var(--status-info-bg, var(--color-surface-secondary)); border: 1px solid var(--status-info-border, var(--color-graphite-300)); border-radius: 6px; font-size: 0.78rem; color: var(--color-text-secondary);">
                  Este cliente ainda não possui projeto cadastrado. A OS pode ser criada sem Projeto e vinculada depois.
                  <a href="#/management/projects" style="color: var(--color-ink); text-decoration: underline; font-weight: 600; margin-left: 4px;">Cadastrar Projeto</a>
                </div>
              ` : ''}
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Área / Unidade Relacionada</label>
              <select id="modal-os-area" ${!currentClientId ? 'disabled' : ''} style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: ${!currentClientId ? 'var(--color-bg-primary)' : 'white'};">
                <option value="">${!currentClientId ? 'Selecione um cliente primeiro...' : 'Nenhuma / Todas as Áreas do Cliente'}</option>
                ${availableAreas.map(a => `<option value="${a.id}" ${this.editingOs?.areaId === a.id ? 'selected' : ''}>${a.name}</option>`).join("")}
              </select>
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Descrição / Escopo *</label>
              <textarea id="modal-os-description" required rows="3" placeholder="Detalhamento do escopo ou contrato..." style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; resize: vertical;">${this.editingOs?.description || ''}</textarea>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Data Início</label>
                <input type="date" id="modal-os-start" value="${this.editingOs?.startDate || ''}" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
              </div>
              <div>
                <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Data Fim</label>
                <input type="date" id="modal-os-end" value="${this.editingOs?.endDate || ''}" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px;">
              <button type="button" class="btn-cancel-os-modal" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 500; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
              <button type="submit" style="background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 600; color: var(--ia-action-fg, #F7F8F8); cursor: pointer;">Salvar</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  /**
   * NOVA SUB-OS — formulário guiado pela hierarquia oficial:
   * OS → PROJETO (só os vinculados à OS) → UNIDADE/ÁREA (só as do Projeto).
   *
   * Os selects apenas ESTREITAM o que é oferecido. Quem valida a combinação
   * continua sendo o serviço (assertSubOsContextIsValid).
   */
  private renderSubOsModal(): string {
    const isEdit = !!this.editingSubOs;
    const esc = (v: unknown) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));

    const workOrderId = this.subOsForm.workOrderId;
    const projectOptions = projectOptionsForWorkOrder(workOrderId, this.workOrders as any, this.projects as any, this.workOrderProjectLinks);
    const unitAreaOptions = unitAreaOptionsForProject(this.subOsForm.projectId, this.unitAreas);

    const workOrder = this.workOrders.find(wo => wo.id === workOrderId) ?? null;
    const project = projectOptions.find(pr => pr.id === this.subOsForm.projectId) ?? null;
    const unitArea = unitAreaOptions.find(a => a.id === this.subOsForm.unitAreaId) ?? null;

    const contextItem = (label: string, value: string) => `
      <div>
        <span style="display:block; font-size:0.68rem; color: var(--ia-fg-muted, #909090); letter-spacing:0.06em;">${label}</span>
        <strong style="display:block; font-size:0.84rem; color: var(--ia-fg, #FFFFFF); margin-top:2px;">${value || "—"}</strong>
      </div>`;

    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px); padding: 20px;">
        <div style="background: var(--ia-bg-raised, #383838); border: 1px solid #5C5C5C; border-radius: 12px; width: 100%; max-width: 760px; max-height: 92vh; overflow-y: auto; padding: 24px;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 18px;">
            <div>
              <h2 style="font-size: 1.2rem; font-weight: 700; color: var(--ia-fg, #FFFFFF); margin: 0;">${isEdit ? "Editar Sub-OS" : "Nova Sub-OS"}</h2>
              <p style="margin: 4px 0 0 0; font-size: 0.8rem; color: var(--ia-fg-soft, #AEAEAE);">Selecione a Ordem de Serviço, o Projeto e a Unidade/Área antes dos dados da Sub-OS.</p>
            </div>
            <button class="btn-close-sub-os-modal" style="background: none; border: none; font-size: 1.4rem; cursor: pointer; color: var(--ia-fg-muted, #909090); line-height: 1;">&times;</button>
          </div>

          <div id="sub-os-modal-error" style="display: none; background: #b71c1c; border-radius: 6px; padding: 10px 14px; font-size: 0.82rem; color: #ffebee; margin-bottom: 16px;"></div>

          <!-- Contexto conferível antes de salvar -->
          <div style="display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; background: var(--ia-bg-panel, #2E2E2E); border: 1px solid #5C5C5C; border-radius: 8px; padding: 14px; margin-bottom: 18px;">
            ${contextItem("Ordem de Serviço", esc(workOrder?.code ?? ""))}
            ${contextItem("Projeto", esc(project ? (project.code ? project.code + " — " + project.name : project.name) : ""))}
            ${contextItem("Unidade / Área", esc(unitArea ? unitArea.code + " — " + unitArea.name : ""))}
          </div>

          <form id="form-sub-os" style="display: flex; flex-direction: column; gap: 16px;">
            <div style="display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px;">
              <div>
                <label class="sub-os-label">Ordem de Serviço *</label>
                <select id="modal-sub-os-parent" class="sub-os-input" required>
                  <option value="">Selecione a OS...</option>
                  ${this.workOrders.map(wo => `<option value="${esc(wo.id)}" ${workOrderId === wo.id ? "selected" : ""}>${esc(wo.code)}${wo.description ? " — " + esc(wo.description) : ""}</option>`).join("")}
                </select>
              </div>
              <div>
                <label class="sub-os-label">Projeto *</label>
                <select id="modal-sub-os-project" class="sub-os-input" ${!workOrderId || projectOptions.length === 0 ? "disabled" : "required"}>
                  <option value="">${!workOrderId ? "Selecione a OS primeiro..." : projectOptions.length === 0 ? "Nenhum Projeto vinculado" : "Selecione o Projeto..."}</option>
                  ${projectOptions.map(pr => `<option value="${esc(pr.id)}" ${this.subOsForm.projectId === pr.id ? "selected" : ""}>${esc(pr.code)} — ${esc(pr.name)}</option>`).join("")}
                </select>
                ${workOrderId && projectOptions.length === 0 ? `
                  <div style="margin-top:6px; font-size:0.74rem; color: var(--ia-fg-soft, #AEAEAE);">
                    ${NO_PROJECTS_FOR_WORK_ORDER_MESSAGE}
                    <button type="button" class="btn-open-link-project" data-id="${esc(workOrderId)}" style="background:none; border:none; color: var(--ia-fg, #EEEEEE); text-decoration:underline; cursor:pointer; font-size:0.74rem; padding:0;">Vincular Projeto</button>
                  </div>` : ""}
              </div>
            </div>

            <div style="display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px;">
              <div>
                <label class="sub-os-label">Unidade / Área</label>
                <select id="modal-sub-os-unit-area" class="sub-os-input" ${!this.subOsForm.projectId || unitAreaOptions.length === 0 ? "disabled" : ""}>
                  <option value="">${!this.subOsForm.projectId ? "Selecione o Projeto primeiro..." : unitAreaOptions.length === 0 ? "Sem Unidades cadastradas" : "Selecione a Unidade/Área..."}</option>
                  ${unitAreaOptions.map(a => `<option value="${esc(a.id)}" ${this.subOsForm.unitAreaId === a.id ? "selected" : ""}>${esc(a.code)} — ${esc(a.name)}</option>`).join("")}
                </select>
                ${this.subOsForm.projectId && unitAreaOptions.length === 0 ? `<div style="margin-top:6px; font-size:0.74rem; color: var(--ia-fg-soft, #AEAEAE);">${NO_UNIT_AREAS_FOR_PROJECT_MESSAGE}</div>` : ""}
              </div>
              <div>
                <label class="sub-os-label">Nº da Sub-OS</label>
                <input type="text" id="modal-sub-os-code" class="sub-os-input" readonly disabled
                  value="${esc(this.editingSubOs?.code ?? "")}"
                  placeholder="${workOrder ? esc(workOrder.code) + "-0n (gerado automaticamente)" : "Gerado automaticamente"}" />
                <div style="margin-top:6px; font-size:0.72rem; color: var(--ia-fg-muted, #909090);">Numerado a partir da OS — não editável.</div>
              </div>
            </div>

            <div>
              <label class="sub-os-label">Descrição *</label>
              <textarea id="modal-sub-os-description" class="sub-os-input" rows="2" required placeholder="Frente específica ou pacote de serviço...">${esc(this.subOsForm.description)}</textarea>
            </div>

            <div style="display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px;">
              <div>
                <label class="sub-os-label">Profissional Responsável</label>
                <select id="modal-sub-os-prof" class="sub-os-input">
                  <option value="">Selecione...</option>
                  ${this.professionals.map(pf => `<option value="${esc(pf.id)}" ${this.subOsForm.professionalId === pf.id ? "selected" : ""}>${esc(pf.name)}${pf.functionTitle ? " — " + esc(pf.functionTitle) : ""}</option>`).join("")}
                </select>
              </div>
              <div>
                <label class="sub-os-label">Status</label>
                <select id="modal-sub-os-status" class="sub-os-input">
                  <option value="OPEN" ${this.subOsForm.status !== "CANCELLED" ? "selected" : ""}>ATIVA</option>
                  <option value="CANCELLED" ${this.subOsForm.status === "CANCELLED" ? "selected" : ""}>INATIVA</option>
                </select>
              </div>
            </div>

            <div style="display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px;">
              <div>
                <label class="sub-os-label">Data Início</label>
                <input type="date" id="modal-sub-os-start" class="sub-os-input" value="${esc(this.subOsForm.startDate)}" />
              </div>
              <div>
                <label class="sub-os-label">Data Fim</label>
                <input type="date" id="modal-sub-os-end" class="sub-os-input" value="${esc(this.subOsForm.endDate)}" />
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 10px; margin-top: 6px;">
              <button type="button" class="btn-cancel-sub-os-modal" style="background: none; border: 1px solid #5C5C5C; border-radius: 6px; padding: 9px 18px; font-size: 0.86rem; font-weight: 600; cursor: pointer; color: var(--ia-fg-soft, #AEAEAE);">Cancelar</button>
              <button type="submit" style="background: var(--color-primary); border: none; border-radius: 6px; padding: 9px 18px; font-size: 0.86rem; font-weight: 700; color: var(--color-text-primary); cursor: pointer;">Salvar Sub-OS</button>
            </div>
          </form>
        </div>
      </div>
      <style>
        .sub-os-label { display: block; font-size: 0.76rem; font-weight: 600; color: var(--ia-fg, #EEEEEE); margin-bottom: 6px; }
        .sub-os-input { width: 100%; box-sizing: border-box; padding: 9px 12px; background: var(--ia-bg-panel, #2E2E2E); border: 1px solid #5C5C5C; border-radius: 6px; color: var(--ia-fg, #FFFFFF); font-size: 0.86rem; }
        .sub-os-input:disabled { color: var(--ia-fg-muted, #909090); cursor: not-allowed; }
        .sub-os-input option { background: var(--ia-bg-panel, #2E2E2E); color: var(--ia-fg, #FFFFFF); }
      </style>`;
  }
  /**
   * "VINCULAR PROJETO À OS" — seletor desenhado, no lugar do prompt numerado.
   *
   * Os Projetos já vinculados continuam na lista, marcados: esconde-los faria
   * o usuário perder de vista o que a OS já tem.
   */
  private renderLinkProjectModal(): string {
    const esc = (v: unknown) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));

    const workOrder = this.workOrders.find(wo => wo.id === this.linkProjectModal.workOrderId) ?? null;
    const options: LinkableProject[] = linkableProjectsForWorkOrder({
      workOrder: workOrder as any,
      projects: this.projects as any,
      links: this.workOrderProjectLinks,
      search: this.linkProjectModal.search
    });
    const selectable = options.filter(option => !option.alreadyLinked).length;
    const chosen = selectableProjectIds(options, this.linkProjectModal.selected).length;

    const client = this.clients.find((c: any) => c.id === workOrder?.clientId);

    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; z-index: 10000; backdrop-filter: blur(2px); padding: 20px;">
        <div style="background: var(--ia-bg-raised, #383838); border: 1px solid #5C5C5C; border-radius: 12px; width: 100%; max-width: 620px; max-height: 90vh; display: flex; flex-direction: column; padding: 24px;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px;">
            <div>
              <h2 style="font-size: 1.1rem; font-weight: 700; color: var(--ia-fg, #FFFFFF); margin: 0;">Vincular Projeto à OS</h2>
              <p style="margin: 4px 0 0 0; font-size: 0.8rem; color: var(--ia-fg-soft, #AEAEAE);">
                OS ${esc(workOrder?.code ?? "—")}${client?.name ? ` · ${esc(client.name)}` : ""}
              </p>
            </div>
            <button type="button" class="btn-close-link-project" style="background: none; border: none; font-size: 1.4rem; cursor: pointer; color: var(--ia-fg-muted, #909090); line-height: 1;">&times;</button>
          </div>

          ${this.linkProjectModal.error ? `
            <div style="background: #b71c1c; border-radius: 6px; padding: 10px 14px; font-size: 0.82rem; color: #ffebee; margin-bottom: 14px;">${esc(this.linkProjectModal.error)}</div>` : ""}

          <input type="text" id="link-project-search" class="sub-os-input" placeholder="Buscar Projeto por código ou nome..."
            value="${esc(this.linkProjectModal.search)}"
            style="width:100%; box-sizing:border-box; padding:9px 12px; background: var(--ia-bg-panel, #2E2E2E); border:1px solid #5C5C5C; border-radius:6px; color: var(--ia-fg, #FFFFFF); font-size:0.86rem; margin-bottom: 12px;" />

          <div style="flex: 1; overflow-y: auto; border: 1px solid #5C5C5C; border-radius: 8px; background: var(--ia-bg-panel, #2E2E2E); min-height: 120px; max-height: 46vh;">
            ${options.length === 0 ? `
              <div style="padding: 20px; font-size: 0.82rem; color: var(--ia-fg-soft, #AEAEAE); text-align: center;">
                ${this.linkProjectModal.search
                  ? "Nenhum Projeto corresponde à busca."
                  : "Nenhum Projeto cadastrado para o Cliente desta Ordem de Serviço."}
              </div>` : options.map(option => `
              <label style="display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-bottom: 1px solid #383838; cursor: ${option.alreadyLinked ? "default" : "pointer"};">
                <input type="checkbox" class="chk-link-project" data-id="${esc(option.id)}"
                  ${option.alreadyLinked ? "disabled" : ""}
                  ${this.linkProjectModal.selected.has(option.id) ? "checked" : ""} />
                <span style="flex: 1; font-size: 0.84rem; color: ${option.alreadyLinked ? "#909090" : "#FFFFFF"};">${esc(option.label)}</span>
                ${option.alreadyLinked ? `<span style="font-size: 0.7rem; font-weight: 700; color: var(--ia-fg-soft, #7FB77E); letter-spacing: 0.05em;">✓ VINCULADO</span>` : ""}
              </label>`).join("")}
          </div>

          <div style="display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-top: 16px; flex-wrap: wrap;">
            <span style="font-size: 0.74rem; color: var(--ia-fg-soft, #AEAEAE);">${chosen} de ${selectable} Projeto(s) disponível(is) selecionado(s)</span>
            <div style="display: flex; gap: 10px;">
              <button type="button" class="btn-cancel-link-project" style="background: none; border: 1px solid #5C5C5C; border-radius: 6px; padding: 9px 18px; font-size: 0.86rem; font-weight: 600; cursor: pointer; color: var(--ia-fg-soft, #AEAEAE);">Cancelar</button>
              <button type="button" id="btn-confirm-link-project" ${chosen === 0 ? "disabled" : ""}
                style="background: ${chosen === 0 ? "#5C5C5C" : "var(--color-primary)"}; border: none; border-radius: 6px; padding: 9px 18px; font-size: 0.86rem; font-weight: 700; color: ${chosen === 0 ? "#909090" : "var(--color-text-primary)"}; cursor: ${chosen === 0 ? "not-allowed" : "pointer"};">Vincular Projetos</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  /**
   * Carrega a cascata para editar uma Sub-OS na ordem da hierarquia:
   * OS → Projetos daquela OS → Projeto → Unidades daquele Projeto → Unidade.
   *
   * Registros anteriores à regra podem não ter Projeto gravado; nesse caso o
   * campo abre vazio para o usuário escolher, em vez de adivinhar um Projeto.
   */
  private blankSubOsForm(overrides: Partial<typeof this.subOsForm> = {}): typeof this.subOsForm {
    return {
      workOrderId: "",
      projectId: "",
      unitAreaId: "",
      professionalId: "",
      description: "",
      status: "OPEN",
      startDate: "",
      endDate: "",
      ...overrides
    };
  }

  /** Guarda o que está digitado antes de a cascata redesenhar a tela. */
  private captureSubOsFormFields(): void {
    const read = (selector: string) => (this.querySelector(selector) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value ?? null;

    const professionalId = read("#modal-sub-os-prof");
    const description = read("#modal-sub-os-description");
    const status = read("#modal-sub-os-status");
    const startDate = read("#modal-sub-os-start");
    const endDate = read("#modal-sub-os-end");

    this.subOsForm = {
      ...this.subOsForm,
      professionalId: professionalId ?? this.subOsForm.professionalId,
      description: description ?? this.subOsForm.description,
      status: status ?? this.subOsForm.status,
      startDate: startDate ?? this.subOsForm.startDate,
      endDate: endDate ?? this.subOsForm.endDate
    };
  }

  private openSubOsFormForEditing(): void {
    const sub = this.editingSubOs;
    if (!sub) {
      this.subOsForm = this.blankSubOsForm();
      return;
    }

    const workOrderId = String(sub.workOrderId ?? "");
    const projectOptions = projectOptionsForWorkOrder(
      workOrderId,
      this.workOrders as any,
      this.projects as any,
      this.workOrderProjectLinks
    );
    const projectId = keepProjectOnWorkOrderChange(String(sub.projectId ?? ""), projectOptions);

    const unitAreaOptions = unitAreaOptionsForProject(projectId, this.unitAreas);
    const unitAreaId = keepUnitAreaOnProjectChange(String(sub.unitAreaId ?? ""), unitAreaOptions);

    this.subOsForm = this.blankSubOsForm({
      workOrderId,
      projectId,
      unitAreaId,
      professionalId: String(sub.professionalId ?? ""),
      description: sub.description ?? "",
      status: sub.status === "CANCELLED" ? "CANCELLED" : "OPEN",
      startDate: sub.startDate ?? "",
      endDate: sub.endDate ?? ""
    });
  }

  /**
   * Caminho da Sub-OS na hierarquia: OS › Projeto › Unidade/Área.
   * O que não está cadastrado aparece como pendência, não como um valor
   * inventado a partir de outro nível.
   */
  private formatSubOsHierarchy(sub: SubWorkOrder): string {
    const esc = (v: unknown) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));

    const workOrderCode = this.workOrders.find(wo => wo.id === sub.workOrderId)?.code || String(sub.workOrderId);
    const project = sub.projectId ? this.projects.find(p => p.id === sub.projectId) : null;
    const unitArea = sub.unitAreaId ? this.unitAreas.find(a => a.id === sub.unitAreaId) : null;

    const pending = (label: string) =>
      `<span style="color: var(--status-warning-solid, #AEAEAE); font-weight: 500;">${label}</span>`;

    const parts = [
      esc(workOrderCode),
      project ? esc(project.code) : pending("Projeto não definido"),
      unitArea ? esc(unitArea.code) : pending("Unidade não definida")
    ];

    return parts.join(' <span style="color: var(--color-graphite-300);">›</span> ');
  }

  private requestContext(clientId?: string): any {
    const ctx = contextStore.get();
    return {
      activeClientId: clientId || ctx.activeClientId || "",
      permissions: ctx.permissions,
      correlationId: "",
      userId: ctx.userId
    };
  }

  private async openCollaborators(subOsId: string): Promise<void> {
    const sub = this.subWorkOrders.find(s => s.id === subOsId) ?? null;
    try {
      const accesses = await platform.adminService.listSubOsCollaborators(
        this.requestContext(sub?.clientId),
        subOsId
      );
      this.collaboratorsModal = {
        isOpen: true,
        subOsId,
        accesses: accesses
          .filter(a => String(a.status ?? "ACTIVE").toUpperCase() === "ACTIVE")
          .map(a => ({ id: a.id, professionalId: String(a.professionalId), status: String(a.status) })),
        error: ""
      };
    } catch (err: any) {
      console.error("[OS] Falha ao carregar os colaboradores da Sub-OS", err);
      this.collaboratorsModal = {
        isOpen: true,
        subOsId,
        accesses: [],
        error: err?.safeMessage || err?.message || "Não foi possível carregar os colaboradores."
      };
    }
    this.render();
  }

  private attachCollaboratorsModalEvents(): void {
    this.querySelectorAll(".btn-close-collaborators").forEach(btn => {
      btn.addEventListener("click", () => {
        this.collaboratorsModal = { isOpen: false, subOsId: "", accesses: [], error: "" };
        this.render();
      });
    });

    const subOsId = this.collaboratorsModal.subOsId;
    const sub = this.subWorkOrders.find(s => s.id === subOsId) ?? null;

    this.querySelectorAll(".btn-grant-collaborator").forEach(btn => {
      btn.addEventListener("click", async () => {
        const professionalId = (btn as HTMLElement).dataset["prof"] || "";
        try {
          await platform.adminService.grantSubOsAccess(this.requestContext(sub?.clientId), subOsId, professionalId);
          await this.openCollaborators(subOsId);
        } catch (err: any) {
          console.error("[OS] Falha ao vincular o colaborador", err);
          this.collaboratorsModal.error = err?.safeMessage || err?.message || "Não foi possível vincular o colaborador.";
          this.render();
        }
      });
    });

    this.querySelectorAll(".btn-revoke-collaborator").forEach(btn => {
      btn.addEventListener("click", async () => {
        const accessId = (btn as HTMLElement).dataset["access"] || "";
        try {
          await platform.adminService.revokeSubOsAccess(this.requestContext(sub?.clientId), accessId);
          await this.openCollaborators(subOsId);
        } catch (err: any) {
          console.error("[OS] Falha ao remover o colaborador", err);
          this.collaboratorsModal.error = err?.safeMessage || err?.message || "Não foi possível remover o colaborador.";
          this.render();
        }
      });
    });
  }

  /** Modal de vínculo: busca, seleção múltipla e confirmação. */
  private attachLinkProjectModalEvents(): void {
    const close = () => {
      this.linkProjectModal = { isOpen: false, workOrderId: "", search: "", selected: new Set<string>(), error: "" };
      this.render();
    };

    this.querySelectorAll(".btn-close-link-project, .btn-cancel-link-project").forEach(btn => {
      btn.addEventListener("click", close);
    });

    const search = this.querySelector("#link-project-search") as HTMLInputElement | null;
    search?.addEventListener("input", () => {
      this.linkProjectModal.search = search.value;
      this.render();
      // Buscar não pode custar o cursor: devolve o foco ao campo redesenhado.
      const again = this.querySelector("#link-project-search") as HTMLInputElement | null;
      if (again) {
        again.focus();
        again.setSelectionRange(again.value.length, again.value.length);
      }
    });

    this.querySelectorAll(".chk-link-project").forEach(box => {
      box.addEventListener("change", () => {
        const id = (box as HTMLElement).dataset["id"] || "";
        if ((box as HTMLInputElement).checked) this.linkProjectModal.selected.add(id);
        else this.linkProjectModal.selected.delete(id);
        this.render();
      });
    });

    this.querySelector("#btn-confirm-link-project")?.addEventListener("click", async () => {
      const workOrderId = this.linkProjectModal.workOrderId;
      const workOrder = this.workOrders.find(wo => wo.id === workOrderId) ?? null;
      const options = linkableProjectsForWorkOrder({
        workOrder: workOrder as any,
        projects: this.projects as any,
        links: this.workOrderProjectLinks,
        search: this.linkProjectModal.search
      });
      const ids = selectableProjectIds(options, this.linkProjectModal.selected);
      if (ids.length === 0) return;

      const button = this.querySelector("#btn-confirm-link-project") as HTMLButtonElement | null;
      if (button) button.disabled = true;

      try {
        const context = this.requestContext(workOrder?.clientId);
        for (const projectId of ids) {
          await platform.rdoService.linkProjectToWorkOrder(context, workOrderId, projectId);
        }
        this.treeExpanded.add(workOrderId);
        this.linkProjectModal = { isOpen: false, workOrderId: "", search: "", selected: new Set<string>(), error: "" };
        await this.loadData();
      } catch (err: any) {
        console.error("[OS] Falha ao vincular o Projeto", err);
        this.linkProjectModal.error = err?.safeMessage || err?.message || "Não foi possível vincular o Projeto.";
        this.render();
      }
    });
  }

  /** Cascata do modal de Sub-OS: trocar a OS ou o Projeto invalida o que está abaixo. */
  private attachSubOsCascadeEvents(): void {
    this.querySelector("#modal-sub-os-parent")?.addEventListener("change", e => {
      this.captureSubOsFormFields();
      const workOrderId = (e.target as HTMLSelectElement).value;
      const projectOptions = projectOptionsForWorkOrder(
        workOrderId,
        this.workOrders as any,
        this.projects as any,
        this.workOrderProjectLinks
      );
      const projectId = keepProjectOnWorkOrderChange(this.subOsForm.projectId, projectOptions);
      const unitAreaOptions = unitAreaOptionsForProject(projectId, this.unitAreas);

      this.subOsForm = {
        ...this.subOsForm,
        workOrderId,
        projectId,
        unitAreaId: keepUnitAreaOnProjectChange(this.subOsForm.unitAreaId, unitAreaOptions)
      };
      this.render();
    });

    this.querySelector("#modal-sub-os-project")?.addEventListener("change", e => {
      this.captureSubOsFormFields();
      const projectId = (e.target as HTMLSelectElement).value;
      const unitAreaOptions = unitAreaOptionsForProject(projectId, this.unitAreas);
      const kept = keepUnitAreaOnProjectChange(this.subOsForm.unitAreaId, unitAreaOptions);

      this.subOsForm = {
        ...this.subOsForm,
        projectId,
        unitAreaId: kept || autoSelectUnitArea(unitAreaOptions)
      };
      this.render();
    });

    this.querySelector("#modal-sub-os-unit-area")?.addEventListener("change", e => {
      this.captureSubOsFormFields();
      this.subOsForm = { ...this.subOsForm, unitAreaId: (e.target as HTMLSelectElement).value };
      this.render();
    });
  }

  private attachEvents(): void {
    // Árvore: expandir/recolher e vincular Projeto à OS.
    this.querySelectorAll(".btn-tree-toggle").forEach(btn => {
      btn.addEventListener("click", () => {
        const id = (btn as HTMLElement).dataset["id"]!;
        if (this.treeExpanded.has(id)) this.treeExpanded.delete(id);
        else this.treeExpanded.add(id);
        this.render();
      });
    });

    this.querySelectorAll(".btn-link-project, .btn-open-link-project").forEach(btn => {
      btn.addEventListener("click", () => {
        const workOrderId = (btn as HTMLElement).dataset["id"] || "";
        if (!workOrderId) return;
        this.linkProjectModal = { isOpen: true, workOrderId, search: "", selected: new Set<string>(), error: "" };
        this.render();
      });
    });

    this.querySelectorAll(".btn-tree-open-project").forEach(btn => {
      btn.addEventListener("click", () => {
        const projectId = (btn as HTMLElement).dataset["project"] || "";
        if (projectId) router.navigate(`/management/projects/${projectId}`);
      });
    });

    // Nova Sub-OS já dentro do Projeto clicado: a cascata abre preenchida.
    this.querySelectorAll(".btn-tree-new-sub-os").forEach(btn => {
      btn.addEventListener("click", () => {
        const workOrderId = (btn as HTMLElement).dataset["id"] || "";
        const projectId = (btn as HTMLElement).dataset["project"] || "";
        const unitAreaOptions = unitAreaOptionsForProject(projectId, this.unitAreas);

        this.editingSubOs = null;
        this.selectedSubOsModalClientId = this.workOrders.find(wo => wo.id === workOrderId)?.clientId || "";
        this.subOsForm = this.blankSubOsForm({ workOrderId, projectId, unitAreaId: autoSelectUnitArea(unitAreaOptions) });
        this.isSubOsModalOpen = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-tree-unlink-project").forEach(btn => {
      btn.addEventListener("click", async () => {
        if ((btn as HTMLButtonElement).disabled) return;
        const workOrderId = (btn as HTMLElement).dataset["id"] || "";
        const projectId = (btn as HTMLElement).dataset["project"] || "";
        if (!workOrderId || !projectId) return;

        const workOrder = this.workOrders.find(wo => wo.id === workOrderId) ?? null;
        try {
          await platform.rdoService.unlinkProjectFromWorkOrder(
            this.requestContext(workOrder?.clientId),
            workOrderId,
            projectId
          );
          this.treeExpanded.add(workOrderId);
          await this.loadData();
        } catch (err: any) {
          console.error("[OS] Falha ao desvincular o Projeto", err);
          this.notice = {
            isOpen: true,
            title: "Não foi possível desvincular",
            message: err?.safeMessage || err?.message || "Não foi possível desvincular o Projeto."
          };
          this.render();
        }
      });
    });

    this.querySelector("#btn-close-notice")?.addEventListener("click", () => {
      this.notice = { isOpen: false, title: "", message: "" };
      this.render();
    });

    this.querySelectorAll(".btn-sub-os-collaborators").forEach(btn => {
      btn.addEventListener("click", async () => {
        const subOsId = (btn as HTMLElement).dataset["id"] || "";
        if (!subOsId) return;
        await this.openCollaborators(subOsId);
      });
    });

    // ATIVAR / INATIVAR a Sub-OS: UPDATE do status, nunca apagar e recriar.
    this.querySelectorAll(".btn-toggle-sub-os").forEach(btn => {
      btn.addEventListener("click", async () => {
        const id = (btn as HTMLElement).dataset["id"] || "";
        const next = (btn as HTMLElement).dataset["next"] === "OPEN" ? "OPEN" : "CANCELLED";
        const sub = this.subWorkOrders.find(s => s.id === id);
        if (!sub) return;

        try {
          await platform.adminService.updateSubWorkOrder(this.requestContext(sub.clientId), {
            ...sub,
            status: next as any
          });
          await this.loadData();
        } catch (err: any) {
          console.error("[OS] Falha ao mudar o status da Sub-OS", err);
          this.notice = {
            isOpen: true,
            title: next === "OPEN" ? "Não foi possível ativar" : "Não foi possível inativar",
            message: err?.safeMessage || err?.message || "Não foi possível alterar o status da Sub-OS."
          };
          this.render();
        }
      });
    });

    this.attachCollaboratorsModalEvents();
    this.attachLinkProjectModalEvents();
    this.attachSubOsCascadeEvents();

    this.querySelector("#tab-os")?.addEventListener("click", () => {
      this.activeTab = "OS";
      this.render();
    });

    this.querySelector("#tab-sub-os")?.addEventListener("click", () => {
      this.activeTab = "SUB_OS";
      this.render();
    });

    // REGRESSÃO V3-B4: troca do filtro de Cliente deve recarregar a listagem
    // do backend, não apenas filtrar em memória. O render() anterior mantinha
    // dados stale e não refletia OS criadas em outros clientes autorizados.
    this.querySelector("#select-client")?.addEventListener("change", async (e) => {
      this.clientFilter = (e.target as HTMLSelectElement).value;
      await this.loadData();
    });

    this.querySelector("#input-search")?.addEventListener("input", (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector("#select-status")?.addEventListener("change", (e) => {
      this.statusFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector("#btn-goto-projects")?.addEventListener("click", () => {
      router.navigate("/management/projects");
    });

    this.querySelector("#select-project")?.addEventListener("change", (e) => {
      this.projectFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector("#btn-clear-project-filter")?.addEventListener("click", () => {
      this.projectFilter = "ALL";
      this.render();
    });

    this.querySelector("#btn-new-os")?.addEventListener("click", () => {
      this.editingOs = null;
      this.selectedOsModalClientId = this.clientFilter !== "ALL" ? this.clientFilter : "";
      this.selectedOsModalProjectId = this.projectFilter !== "ALL" ? this.projectFilter : "";
      this.isOsModalOpen = true;
      this.render();
    });

    this.querySelector("#btn-new-sub-os")?.addEventListener("click", () => {
      this.editingSubOs = null;
      this.selectedSubOsModalClientId = "";
      this.subOsForm = this.blankSubOsForm();
      this.isSubOsModalOpen = true;
      this.render();
    });

    this.querySelectorAll(".btn-edit-os").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.editingOs = this.workOrders.find(wo => wo.id === id) || null;
        this.selectedOsModalClientId = this.editingOs?.clientId || "";
        this.selectedOsModalProjectId = this.editingOs?.projectId || "";
        this.isOsModalOpen = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-inactivate-os").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          type: "OS",
          id: id,
          title: "Inativar Ordem de Serviço",
          message: "Deseja realmente cancelar esta Ordem de Serviço?",
          error: ""
        };
        this.render();
      });
    });

    this.querySelectorAll(".btn-delete-os").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          type: "OS",
          id: id,
          title: "Excluir Ordem de Serviço",
          message: "Atenção: A exclusão só será permitida se não houver vínculos ativos. Deseja prosseguir com a exclusão?",
          error: ""
        };
        this.render();
      });
    });

    this.querySelectorAll(".btn-edit-sub-os").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.editingSubOs = this.subWorkOrders.find(sub => sub.id === id) || null;
        this.selectedSubOsModalClientId = this.editingSubOs?.clientId || "";
        this.openSubOsFormForEditing();
        this.isSubOsModalOpen = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-inactivate-sub-os").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          type: "SUB_OS",
          id: id,
          title: "Inativar Sub-OS",
          message: "Deseja realmente cancelar esta Sub-OS?",
          error: ""
        };
        this.render();
      });
    });

    this.querySelectorAll(".btn-delete-sub-os").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          type: "SUB_OS",
          id: id,
          title: "Excluir Sub-OS",
          message: "Atenção: A exclusão só será permitida se não houver vínculos ativos. Deseja prosseguir com a exclusão?",
          error: ""
        };
        this.render();
      });
    });

    this.querySelectorAll(".btn-close-os-modal, .btn-cancel-os-modal").forEach(b => {
      b.addEventListener("click", () => {
        this.isOsModalOpen = false;
        this.render();
      });
    });

    this.querySelectorAll(".btn-close-sub-os-modal, .btn-cancel-sub-os-modal").forEach(b => {
      b.addEventListener("click", () => {
        this.isSubOsModalOpen = false;
        this.render();
      });
    });

    // Cascading Client change in OS Modal
    this.querySelector("#modal-os-client")?.addEventListener("change", (e) => {
      this.selectedOsModalClientId = (e.target as HTMLSelectElement).value;
      this.selectedOsModalProjectId = "";
      const projSelect = this.querySelector("#modal-os-project") as HTMLSelectElement;
      const areaSelect = this.querySelector("#modal-os-area") as HTMLSelectElement;

      if (projSelect) {
        if (!this.selectedOsModalClientId) {
          projSelect.disabled = true;
          projSelect.style.background = 'var(--color-bg-primary)';
          projSelect.innerHTML = '<option value="">Selecione um cliente primeiro...</option>';
        } else {
          projSelect.disabled = false;
          projSelect.style.background = 'white';
          const clientProjects = this.projects.filter(p => p.clientId === this.selectedOsModalClientId);
          if (clientProjects.length === 0) {
            projSelect.innerHTML = '<option value="">Nenhum (vincular depois)</option>';
          } else {
            projSelect.innerHTML = '<option value="">Nenhum (vincular depois)</option>' +
              clientProjects.map(p => `<option value="${p.id}">${p.code} - ${p.name}</option>`).join("");
          }
        }
      }

      if (areaSelect) {
        if (!this.selectedOsModalClientId) {
          areaSelect.disabled = true;
          areaSelect.style.background = 'var(--color-bg-primary)';
          areaSelect.innerHTML = '<option value="">Selecione um cliente primeiro...</option>';
        } else {
          areaSelect.disabled = false;
          areaSelect.style.background = 'white';
          const clientAreas = this.areas.filter(a => a.clientId === this.selectedOsModalClientId);
          areaSelect.innerHTML = '<option value="">Nenhuma / Todas as Áreas do Cliente</option>' +
            clientAreas.map(a => `<option value="${a.id}">${a.name}</option>`).join("");
        }
      }
    });

    this.querySelector("#form-os")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const errorDiv = this.querySelector("#os-modal-error") as HTMLElement;
      if (errorDiv) {
        errorDiv.style.display = "none";
        errorDiv.textContent = "";
      }

      const clientSelect = this.querySelector("#modal-os-client") as HTMLSelectElement;
      const clientId = this.editingOs ? this.editingOs.clientId : (clientSelect?.value || "");
      if (!clientId) {
        if (errorDiv) {
          errorDiv.style.display = "block";
          errorDiv.textContent = "SELECIONE O CLIENTE.";
        }
        return;
      }

      // Projeto é opcional (CLAUDE.md: CLIENTE → OS → PROJETO, OS não depende
      // de Projeto para existir) — pode ser vinculado depois em "Vincular Projeto".
      // O select reflete sempre o estado atual (inclusive "Nenhum"), então o
      // valor dele é a única fonte — nunca cai de volta para o Projeto antigo
      // da OS quando o usuário limpa a seleção.
      const projectSelect = this.querySelector("#modal-os-project") as HTMLSelectElement;
      const projectId = (projectSelect?.value || "").trim() || null;

      const areaId = (this.querySelector("#modal-os-area") as HTMLSelectElement).value || null;
      const description = (this.querySelector("#modal-os-description") as HTMLTextAreaElement).value;
      const startDate = (this.querySelector("#modal-os-start") as HTMLInputElement).value || null;
      const endDate = (this.querySelector("#modal-os-end") as HTMLInputElement).value || null;

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: clientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      const submitBtn = this.querySelector("#form-os button[type='submit']") as HTMLButtonElement;
      if (submitBtn) submitBtn.disabled = true;

      try {
        // [ONLINE-TRACE][OS-FORM] Diagnóstico temporário seguro — captura
        // clientId real do formulário antes do RPC para comparar com backend.
        console.log("[ONLINE-TRACE][OS-FORM]", JSON.stringify({
          action: this.editingOs ? "UPDATE" : "CREATE",
          formClientId: clientId ?? null,
          activeClientId: (window as any).contextStore?.get?.()?.activeClientId ?? null,
          dummyContextActiveClientId: dummyContext?.activeClientId ?? null,
          projectId: projectId ?? null,
          areaId: areaId ?? null
        }));
        if (this.editingOs) {
          await platform.adminService.updateWorkOrder(dummyContext, {
            ...this.editingOs,
            clientId: clientId as any,
            projectId,
            areaId: areaId as any,
            description,
            startDate,
            endDate
          });
        } else {
          await platform.adminService.createWorkOrder(dummyContext, {
            clientId: clientId as any,
            projectId,
            areaId,
            description,
            startDate,
            endDate
          });
        }
        // [ONLINE-TRACE][OS-SAVE-OK] Confirma que o save retornou sucesso
        // antes de recarregar a listagem.
        console.log("[ONLINE-TRACE][OS-SAVE-OK]", JSON.stringify({
          formClientId: clientId ?? null,
          willReloadWithClientFilter: this.clientFilter ?? null
        }));
        this.isOsModalOpen = false;
        await this.loadData();
      } catch (err: any) {
        if (errorDiv) {
          errorDiv.style.display = "block";
          errorDiv.textContent = err.message || "Erro ao salvar Ordem de Serviço.";
        }
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    });

    this.querySelector("#form-sub-os")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const errorDiv = this.querySelector("#sub-os-modal-error") as HTMLElement;
      if (errorDiv) {
        errorDiv.style.display = "none";
        errorDiv.textContent = "";
      }

      const fail = (message: string) => {
        if (errorDiv) {
          errorDiv.style.display = "block";
          errorDiv.textContent = message;
        }
      };

      const workOrderId = (this.querySelector("#modal-sub-os-parent") as HTMLSelectElement).value;
      if (!workOrderId) {
        fail("SELECIONE A ORDEM DE SERVIÇO.");
        return;
      }

      // O Cliente vem da OS — a hierarquia já o define, não há por que perguntar.
      const parentWorkOrder = this.workOrders.find(wo => wo.id === workOrderId) ?? null;
      const clientId = parentWorkOrder?.clientId || this.editingSubOs?.clientId || "";
      if (!clientId) {
        fail("A Ordem de Serviço selecionada não possui Cliente definido.");
        return;
      }

      const projectId = (this.querySelector("#modal-sub-os-project") as HTMLSelectElement)?.value || "";
      if (!projectId) {
        fail("SELECIONE O PROJETO DA SUB-OS.");
        return;
      }

      const unitAreaId = (this.querySelector("#modal-sub-os-unit-area") as HTMLSelectElement)?.value || null;

      const professionalId = (this.querySelector("#modal-sub-os-prof") as HTMLSelectElement).value;
      if (!professionalId) {
        fail("SELECIONE O PROFISSIONAL RESPONSÁVEL.");
        return;
      }

      const description = (this.querySelector("#modal-sub-os-description") as HTMLTextAreaElement).value;
      const startDate = (this.querySelector("#modal-sub-os-start") as HTMLInputElement).value || null;
      const endDate = (this.querySelector("#modal-sub-os-end") as HTMLInputElement).value || null;
      const status = (this.querySelector("#modal-sub-os-status") as HTMLSelectElement)?.value || "OPEN";

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: clientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      const submitBtn = this.querySelector("#form-sub-os button[type='submit']") as HTMLButtonElement | null;
      if (submitBtn) submitBtn.disabled = true;

      try {
        // A tela filtrada não substitui a validação: o serviço confere se o
        // Projeto está mesmo vinculado à OS e se a Unidade é daquele Projeto.
        await platform.rdoService.assertSubOsContextIsValid(dummyContext, {
          workOrderId,
          projectId,
          unitAreaId
        });

        if (this.editingSubOs) {
          await platform.adminService.transferSubWorkOrder(dummyContext, {
            subWorkOrderId: this.editingSubOs.id,
            newProfessionalId: professionalId,
            reason: "Atualização via formulário de edição"
          }).catch(async () => {});

          await platform.adminService.updateSubWorkOrder(dummyContext, {
            ...this.editingSubOs,
            clientId: clientId as any,
            professionalId: professionalId as any,
            workOrderId: workOrderId as any,
            projectId,
            unitAreaId,
            status: status as any,
            description,
            startDate,
            endDate
          });
        } else {
          await platform.adminService.createSubWorkOrder(dummyContext, {
            clientId: clientId as any,
            workOrderId,
            projectId,
            unitAreaId,
            professionalId,
            description,
            startDate,
            endDate
          });
        }
        this.isSubOsModalOpen = false;
        await this.loadData();
      } catch (err: any) {
        fail(err?.safeMessage || err?.message || "Erro ao salvar Sub-OS.");
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    });

    this.querySelector("#btn-confirm-delete")?.addEventListener("click", async () => {
      const { type, id, title } = this.deleteModal;
      const isInactivate = title.includes("Inativar");
      
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId || "",
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      const btnConfirm = this.querySelector("#btn-confirm-delete") as HTMLButtonElement;
      if (btnConfirm) btnConfirm.disabled = true;

      try {
        if (type === "OS") {
          const wo = this.workOrders.find(w => w.id === id);
          dummyContext.activeClientId = wo?.clientId || ctx.activeClientId;
          if (isInactivate) {
            await platform.adminService.inactivateWorkOrder(dummyContext, id);
          } else {
            await platform.adminService.deleteWorkOrder(dummyContext, id);
          }
        } else if (type === "SUB_OS") {
          const swo = this.subWorkOrders.find(s => s.id === id);
          dummyContext.activeClientId = swo?.clientId || ctx.activeClientId;
          if (isInactivate) {
            await platform.adminService.inactivateSubWorkOrder(dummyContext, id);
          } else {
            await platform.adminService.deleteSubWorkOrder(dummyContext, id);
          }
        }
        this.deleteModal.isOpen = false;
        await this.loadData();
      } catch (e: any) {
        this.deleteModal.error = e.message || "Erro ao executar ação.";
        this.render();
      } finally {
        if (btnConfirm) btnConfirm.disabled = false;
      }
    });

    this.querySelector("#btn-cancel-delete")?.addEventListener("click", () => {
      this.deleteModal.isOpen = false;
      this.render();
    });
  }

  /**
   * "COLABORADORES DA SUB-OS" — quem pode apontar RDO nela.
   *
   * O responsável da Sub-OS já é colaborador por definição e aparece marcado
   * como tal: dar-lhe um acesso extra seria registrar duas vezes a mesma coisa.
   */
  private renderCollaboratorsModal(): string {
    const esc = (v: unknown) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));

    const sub = this.subWorkOrders.find(s => s.id === this.collaboratorsModal.subOsId) ?? null;
    const responsibleId = String(sub?.professionalId ?? "");
    const granted = new Map(this.collaboratorsModal.accesses.map(a => [a.professionalId, a]));

    const rows = [...this.professionals]
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
      .filter(pf => !sub?.clientId || pf.clientId === sub.clientId);

    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; z-index: 10000; backdrop-filter: blur(2px); padding: 20px;">
        <div style="background: var(--ia-bg-raised, #383838); border: 1px solid #5C5C5C; border-radius: 12px; width: 100%; max-width: 600px; max-height: 90vh; display: flex; flex-direction: column; padding: 24px;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px;">
            <div>
              <h2 style="font-size: 1.1rem; font-weight: 700; color: var(--ia-fg, #FFFFFF); margin: 0;">Colaboradores da Sub-OS</h2>
              <p style="margin: 4px 0 0 0; font-size: 0.8rem; color: var(--ia-fg-soft, #AEAEAE);">${esc(sub?.code ?? "—")}${sub?.description ? ` · ${esc(sub.description)}` : ""}</p>
            </div>
            <button type="button" class="btn-close-collaborators" style="background: none; border: none; font-size: 1.4rem; cursor: pointer; color: var(--ia-fg-muted, #909090); line-height: 1;">&times;</button>
          </div>

          ${this.collaboratorsModal.error ? `
            <div style="background: #b71c1c; border-radius: 6px; padding: 10px 14px; font-size: 0.82rem; color: #ffebee; margin-bottom: 14px;">${esc(this.collaboratorsModal.error)}</div>` : ""}

          <div style="flex: 1; overflow-y: auto; border: 1px solid #5C5C5C; border-radius: 8px; background: var(--ia-bg-panel, #2E2E2E); max-height: 52vh;">
            ${rows.length === 0 ? `
              <div style="padding: 20px; font-size: 0.82rem; color: var(--ia-fg-soft, #AEAEAE); text-align: center;">Nenhum Profissional cadastrado para o Cliente desta Sub-OS.</div>
            ` : rows.map(pf => {
              const access = granted.get(pf.id);
              const isResponsible = pf.id === responsibleId;
              return `
                <div style="display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-bottom: 1px solid #383838;">
                  <span style="flex: 1; font-size: 0.84rem; color: var(--ia-fg, #FFFFFF);">
                    ${esc(pf.name)}${pf.functionTitle ? `<span style="color: var(--ia-fg-muted, #909090);"> — ${esc(pf.functionTitle)}</span>` : ""}
                  </span>
                  ${isResponsible
                    ? `<span style="font-size: 0.7rem; font-weight: 700; color: var(--ia-fg-soft, #7FB77E); letter-spacing: 0.05em;">RESPONSÁVEL</span>`
                    : access
                      ? `<button type="button" class="btn-revoke-collaborator" data-access="${esc(access.id)}" style="background:none; border:1px solid #5C5C5C; border-radius:4px; color: var(--ia-fg-soft, #AEAEAE); cursor:pointer; font-size:0.7rem; padding:3px 10px;">REMOVER</button>`
                      : `<button type="button" class="btn-grant-collaborator" data-prof="${esc(pf.id)}" style="background:none; border:1px solid var(--color-primary); border-radius:4px; color: var(--ia-primary); cursor:pointer; font-size:0.7rem; padding:3px 10px;">ADICIONAR</button>`}
                </div>`;
            }).join("")}
          </div>

          <div style="display: flex; justify-content: flex-end; margin-top: 16px;">
            <button type="button" class="btn-close-collaborators" style="background: var(--color-primary); border: none; border-radius: 6px; padding: 9px 22px; font-size: 0.86rem; font-weight: 700; color: var(--color-text-primary); cursor: pointer;">Concluir</button>
          </div>
        </div>
      </div>`;
  }

  /** Aviso simples da tela — nada de alert() nativo no fluxo oficial. */
  private renderNoticeModal(): string {
    const esc = (v: unknown) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; z-index: 10001; backdrop-filter: blur(2px); padding: 20px;">
        <div style="background: var(--ia-bg-raised, #383838); border: 1px solid #5C5C5C; border-radius: 12px; width: 100%; max-width: 440px; padding: 24px; text-align: center;">
          <h2 style="font-size: 1.05rem; font-weight: 700; color: var(--ia-fg, #FFFFFF); margin: 0 0 12px 0;">${esc(this.notice.title)}</h2>
          <p style="color: var(--ia-fg-soft, #AEAEAE); font-size: 0.86rem; margin: 0 0 20px 0;">${esc(this.notice.message)}</p>
          <button type="button" id="btn-close-notice" style="background: var(--color-primary); border: none; border-radius: 6px; padding: 9px 22px; font-size: 0.86rem; font-weight: 700; color: var(--color-text-primary); cursor: pointer;">Entendi</button>
        </div>
      </div>`;
  }

  private renderDeleteModal(): string {
    const { title, message, error } = this.deleteModal;
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 450px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); text-align: center;">
          <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 16px 0;">${title}</h2>
          <p style="color: var(--color-text-secondary); font-size: 0.95rem; margin-bottom: 24px;">${message}</p>
          ${error ? `<div style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px; padding: 10px 14px; font-size: 0.8125rem; color: var(--status-danger-text); margin-bottom: 16px; text-align: left;">${error}</div>` : ''}
          <div style="display: flex; justify-content: center; gap: 12px;">
            <button id="btn-cancel-delete" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 24px; font-size: 0.875rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
            <button id="btn-confirm-delete" style="background: var(--status-danger-solid); border: none; border-radius: 6px; padding: 8px 24px; font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary); cursor: pointer;">Confirmar</button>
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get("app-admin-os-page")) {
  customElements.define("app-admin-os-page", AdminOsPage);
}



